import { useAuth0 } from '@auth0/auth0-react';
import {
  AppShell,
  Box,
  Burger,
  Button,
  Center,
  Group,
  Image,
  Loader,
  NavLink,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconBooks,
  IconBrandWikipedia,
  IconCameraSearch,
  IconChartBar,
  IconClockCog,
  IconGitMerge,
  IconHeart,
  IconHistory,
  IconHome,
  IconLayoutDashboard,
  IconList,
  IconLink,
  IconPlayerPlay,
  IconPlus,
  IconTruck,
  IconTrophy,
  IconUpload,
  IconUsers,
} from '@tabler/icons-react';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useUserStore } from '../../stores/userStore';
import { ArticleStatus } from '../../types';
import { captureAuthReturn, consumeAuthReturn } from '../../utils/authReturn';
import AuthenticationSetupErrorPage from '../auth/AuthenticationSetupErrorPage';
import FullScreenLoading from '../auth/FullScreenLoading';
import IdentityLinkingPage from '../auth/IdentityLinkingPage';
import LoginButton from '../auth/LoginButton';
import LogoutHandler from '../auth/LogoutHandler';
import MarketingOnboardingPage from '../auth/MarketingOnboardingPage';
import SuspendedAccountPage from '../auth/SuspendedAccountPage';
import TermsAcceptancePage from '../auth/TermsAcceptancePage';
import { BrandName } from '../shared/BrandName';
import GlobalSearch from '../shared/GlobalSearch';
import { PageTitle } from '../shared/PageTitle';
import UserAvatar from '../shared/UserAvatar';

const RETROCRITIC_ORIGIN = import.meta.env.VITE_RETROCRITIC_ORIGIN || 'https://retrocritic.com';

// Lazy load components for code splitting
const Landing = lazy(() => import('../pages/Landing'));
const GameListContainer = lazy(() => import('../collection/GameListContainer'));
const ArticleDetailPage = lazy(() => import('../collection/detail/ArticleDetailPage'));
const ArticleWizard = lazy(() => import('../collection/wizard/ArticleWizard'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Analytics = lazy(() => import('../collection/Analytics'));
const UploadLandingPage = lazy(() =>
  import('../upload/UploadLandingPage').then((m) => ({ default: m.UploadLandingPage })),
);
const UploadDetailsPage = lazy(() =>
  import('../upload/UploadDetailsPage').then((m) => ({ default: m.UploadDetailsPage })),
);
const GameDbGamePage = lazy(() => import('../gamedb/GameDbGamePage').then((m) => ({ default: m.GameDbGamePage })));
const GameDbCompanyPage = lazy(() =>
  import('../gamedb/GameDbCompanyPage').then((m) => ({ default: m.GameDbCompanyPage })),
);
const ListsPage = lazy(() => import('../lists/ListsPage').then((m) => ({ default: m.ListsPage })));
const ListsLandingPage = lazy(() => import('../lists/ListsLandingPage').then((m) => ({ default: m.ListsLandingPage })));
const UserProfile = lazy(() => import('../profile/UserProfile'));
const JobsPage = lazy(() => import('../admin/JobsPage'));
const AdminOverviewPage = lazy(() => import('../admin/AdminOverviewPage'));
const CoverSearchTestPage = lazy(() => import('../admin/CoverSearchTestPage'));
const UserAdminPage = lazy(() => import('../admin/UserAdminPage'));
const UserAdminDetailsPage = lazy(() => import('../admin/UserAdminDetailsPage'));
const MissingWikipediaPage = lazy(() => import('../admin/MissingWikipediaPage'));
const SlugCollisionsPage = lazy(() => import('../admin/SlugCollisionsPage'));
const SteamMappingsPage = lazy(() => import('../admin/SteamMappingsPage'));
const SteamBasicCompletionsPage = lazy(() => import('../admin/SteamBasicCompletionsPage'));
const BacklogKanbanPage = lazy(() =>
  import('../backlog/BacklogKanbanPage').then((m) => ({ default: m.BacklogKanbanPage })),
);
const BacklogTabbedPage = lazy(() =>
  import('../backlog/BacklogTabbedPage').then((m) => ({ default: m.BacklogTabbedPage })),
);
const BacklogDashboardPage = lazy(() =>
  import('../backlog/BacklogDashboardPage').then((m) => ({ default: m.BacklogDashboardPage })),
);
const BacklogPlayedHistoryPage = lazy(() =>
  import('../backlog/BacklogPlayedHistoryPage').then((m) => ({ default: m.BacklogPlayedHistoryPage })),
);
const BacklogYearReviewPage = lazy(() =>
  import('../backlog/BacklogYearReviewPage').then((m) => ({ default: m.BacklogYearReviewPage })),
);

export default function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const isDesktop = useMediaQuery('(min-width: 1200px)');
  const { isAuthenticated, getAccessTokenSilently, isLoading: auth0Loading } = useAuth0();
  const location = useLocation();
  const {
    state: userState,
    checkUserState,
    isLoading: userStateLoading,
    error: userStateError,
    errorCode: userStateErrorCode,
    features,
    marketingOnboardingCompleted,
    hasAcceptedCurrentTerms,
  } = useUserStore();
  const isGameList =
    location.pathname === '/collection' ||
    location.pathname === '/collection/wanted' ||
    location.pathname === '/collection/awaiting';
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    captureAuthReturn(window.location.search, window.sessionStorage, RETROCRITIC_ORIGIN);
  }, []);

  useEffect(() => {
    if (
      !auth0Loading &&
      isAuthenticated &&
      userState === 'active' &&
      hasAcceptedCurrentTerms &&
      marketingOnboardingCompleted
    ) {
      const returnTo = consumeAuthReturn(window.sessionStorage);
      if (returnTo) window.location.assign(returnTo);
    }
  }, [auth0Loading, hasAcceptedCurrentTerms, isAuthenticated, marketingOnboardingCompleted, userState]);

  // Check user state when authenticated
  useEffect(() => {
    if (isAuthenticated && !auth0Loading) {
      checkUserState(getAccessTokenSilently);
    }
  }, [isAuthenticated, auth0Loading, checkUserState, getAccessTokenSilently]); // Only check on auth state changes, not route changes

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return;
    previousPathnameRef.current = location.pathname;

    // Reset scroll position when location changes
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0;
    }
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  // Handle navigation - close mobile menu
  const handleNavigation = (_path: string) => {
    close();
  };

  // If on logout route, bypass all auth checks and show logout handler directly
  if (location.pathname === '/logout') {
    return <LogoutHandler />;
  }

  // Show loading spinner while Auth0 or user state is loading
  if (auth0Loading || (isAuthenticated && userStateLoading)) {
    return <FullScreenLoading message={isAuthenticated ? 'Setting up your account…' : 'Loading The Game Project…'} />;
  }

  if (isAuthenticated && userStateErrorCode === 'email_not_verified') {
    return <AuthenticationSetupErrorPage />;
  }

  // Handle onboarding flow based on user state
  if (isAuthenticated && userState !== 'active') {
    switch (userState) {
      case 'waiting_identity_link':
        return <IdentityLinkingPage />;
      case 'suspended':
        return <SuspendedAccountPage />;
      case 'loading':
        // Check if there's an authentication error
        if (userStateError === 'Authentication required' || userStateError === 'Authentication expired') {
          return (
            <Center h="100vh">
              <Stack align="center" gap="md">
                <Loader size="lg" />
                <Text>Session expired. Redirecting to login...</Text>
                <Button
                  variant="outline"
                  onClick={() => {
                    window.location.href = '/logout';
                  }}
                  mt="md"
                >
                  Click here if not redirected automatically
                </Button>
              </Stack>
            </Center>
          );
        }
        return <FullScreenLoading message="Checking your account…" />;
      default:
        return (
          <Center h="100vh">
            <Stack align="center" gap="md">
              <Text>Unknown account state. Please try refreshing the page.</Text>
            </Stack>
          </Center>
        );
    }
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<FullScreenLoading message="Loading The Game Project…" />}>
        <Landing />
      </Suspense>
    );
  }

  if (userState === 'active' && !hasAcceptedCurrentTerms) {
    return <TermsAcceptancePage />;
  }

  if (userState === 'active' && !marketingOnboardingCompleted) {
    return <MarketingOnboardingPage />;
  }

  return (
    <>
      <PageTitle />
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 300,
          breakpoint: 'xl',
          collapsed: { mobile: !opened },
        }}
        padding={{ base: 'xs', sm: 'md' }}
        styles={{
          navbar: {
            maxWidth: 400,
          },
          main: {
            height: 'calc(100vh - var(--app-shell-header-height))',
            overflow: 'hidden',
          },
        }}
      >
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Group wrap="nowrap">
              <Burger opened={opened} onClick={toggle} hiddenFrom="xl" size="sm" />
              <Group gap="xs" wrap="nowrap">
                <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Group gap="xs" wrap="nowrap" style={{ cursor: 'pointer' }}>
                    <Image src="/data/logo.svg" alt="Logo" width={32} height={32} />
                    <Box visibleFrom="md">
                      <BrandName />
                    </Box>
                  </Group>
                </Link>
              </Group>
            </Group>
            {isAuthenticated && userState === 'active' && !isGameList && <GlobalSearch />}
            <Group gap="md">{isAuthenticated && userState === 'active' ? <UserAvatar /> : <LoginButton />}</Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar p="md">
          {isAuthenticated && userState === 'active' && (
            <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <ScrollArea h="calc(100% - 40px)" type="auto" offsetScrollbars>
                <Stack>
                  {/* Home Section */}
                  <Box>
                    <NavLink
                      component={Link}
                      to="/"
                      label="Home"
                      leftSection={<IconHome size={20} />}
                      active={location.pathname === '/'}
                      variant="filled"
                      color="myColor"
                      onClick={() => handleNavigation('/')}
                    />
                  </Box>

                  {/* Collection Section */}
                  <Box>
                    <Text
                      size="sm"
                      fw={600}
                      c="dimmed"
                      mb="xs"
                      style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
                    >
                      Collection
                    </Text>
                    <Stack gap={0}>
                      <NavLink
                        component={Link}
                        to="/add"
                        label="Add Item"
                        leftSection={<IconPlus size={20} />}
                        active={location.pathname.startsWith('/add')}
                        variant="filled"
                        color="myColor"
                        onClick={() => handleNavigation('/add')}
                      />
                      <NavLink
                        component={Link}
                        to="/collection"
                        label="Your Collection"
                        leftSection={<IconBooks size={20} />}
                        active={location.pathname === '/collection'}
                        variant="filled"
                        color="myColor"
                        onClick={() => handleNavigation('/collection')}
                      />
                      <Box pl="md">
                        <NavLink
                          component={Link}
                          to="/collection/awaiting"
                          label="Awaiting Arrival"
                          leftSection={<IconTruck size={20} />}
                          active={location.pathname === '/collection/awaiting'}
                          variant="filled"
                          color="myColor"
                          onClick={() => handleNavigation('/collection/awaiting')}
                        />
                        <NavLink
                          component={Link}
                          to="/collection/wanted"
                          label="Wanted"
                          leftSection={<IconHeart size={20} />}
                          active={location.pathname === '/collection/wanted'}
                          variant="filled"
                          color="myColor"
                          onClick={() => handleNavigation('/collection/wanted')}
                        />
                      </Box>
                      <NavLink
                        component={Link}
                        to="/analytics"
                        label="Analytics"
                        leftSection={<IconChartBar size={20} />}
                        active={location.pathname === '/analytics'}
                        variant="filled"
                        color="myColor"
                        onClick={() => handleNavigation('/analytics')}
                      />
                      <NavLink
                        component={Link}
                        to="/upload"
                        label="Intelligent Import"
                        leftSection={<IconUpload size={20} />}
                        active={location.pathname.startsWith('/upload')}
                        variant="filled"
                        color="myColor"
                        onClick={() => handleNavigation('/upload')}
                      />
                    </Stack>
                  </Box>

                  {/* Gameplay Section */}
                  <Box>
                    <Text
                      size="sm"
                      fw={600}
                      c="dimmed"
                      mb="xs"
                      style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
                    >
                      Gameplay
                    </Text>
                    <Stack gap={0}>
                      <NavLink
                        component={Link}
                        to={isDesktop ? '/backlog/kanban' : '/backlog/tabs'}
                        label="Your Backlog"
                        leftSection={<IconPlayerPlay size={20} />}
                        active={
                          location.pathname.startsWith('/backlog') &&
                          !location.pathname.startsWith('/backlog/played') &&
                          location.pathname !== '/backlog/dashboard'
                        }
                        variant="filled"
                        color="myColor"
                        onClick={() => handleNavigation(isDesktop ? '/backlog/kanban' : '/backlog/tabs')}
                      />
                      <NavLink
                        component={Link}
                        to="/backlog/played"
                        label="Your Replay"
                        leftSection={<IconHistory size={20} />}
                        active={location.pathname.startsWith('/backlog/played')}
                        variant="filled"
                        color="myColor"
                        onClick={() => handleNavigation('/backlog/played')}
                      />
                      <NavLink
                        component={Link}
                        to="/lists"
                        label="Your Lists"
                        leftSection={<IconList size={20} />}
                        active={location.pathname.startsWith('/lists')}
                        variant="filled"
                        color="myColor"
                        onClick={() => handleNavigation('/lists')}
                      />
                    </Stack>
                  </Box>

                  {/* Admin Section - Only visible if user has admin features */}
                  {(features.job_queue_stats?.available ||
                    features.admin_overview?.available ||
                    features.user_admin?.available ||
                    features.slug_collision_review?.available ||
                    features.steam_mapping_admin?.available ||
                    features.steam_basic_completion_admin?.available) && (
                    <Box>
                      <Text
                        size="sm"
                        fw={600}
                        c="dimmed"
                        mb="xs"
                        style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
                      >
                        Admin
                      </Text>
                      <Stack gap={0}>
                        {features.admin_overview?.available && (
                          <NavLink
                            component={Link}
                            to="/admin/cover-search"
                            label="Cover Search"
                            leftSection={<IconCameraSearch size={20} />}
                            active={location.pathname === '/admin/cover-search'}
                            variant="filled"
                            color="myColor"
                            onClick={() => handleNavigation('/admin/cover-search')}
                          />
                        )}
                        {features.admin_overview?.available && (
                          <NavLink
                            component={Link}
                            to="/admin/overview"
                            label="Overview"
                            leftSection={<IconLayoutDashboard size={20} />}
                            active={location.pathname === '/admin/overview'}
                            variant="filled"
                            color="myColor"
                            onClick={() => handleNavigation('/admin/overview')}
                          />
                        )}
                        {features.admin_overview?.available && (
                          <NavLink
                            component={Link}
                            to="/admin/missing-wikipedia"
                            label="Missing Wikipedia"
                            leftSection={<IconBrandWikipedia size={20} />}
                            active={location.pathname === '/admin/missing-wikipedia'}
                            variant="filled"
                            color="myColor"
                            onClick={() => handleNavigation('/admin/missing-wikipedia')}
                          />
                        )}
                        {features.job_queue_stats?.available && (
                          <NavLink
                            component={Link}
                            to="/admin/jobs"
                            label="Jobs"
                            leftSection={<IconClockCog size={20} />}
                            active={location.pathname === '/admin/jobs'}
                            variant="filled"
                            color="myColor"
                            onClick={() => handleNavigation('/admin/jobs')}
                          />
                        )}
                        {features.user_admin?.available && (
                          <NavLink
                            component={Link}
                            to="/admin/users"
                            label="Users"
                            leftSection={<IconUsers size={20} />}
                            active={location.pathname.startsWith('/admin/users')}
                            variant="filled"
                            color="myColor"
                            onClick={() => handleNavigation('/admin/users')}
                          />
                        )}
                        {features.slug_collision_review?.available && (
                          <NavLink
                            component={Link}
                            to="/admin/slug-collisions"
                            label="Slug Collisions"
                            leftSection={<IconGitMerge size={20} />}
                            active={location.pathname === '/admin/slug-collisions'}
                            variant="filled"
                            color="myColor"
                            onClick={() => handleNavigation('/admin/slug-collisions')}
                          />
                        )}
                        {features.steam_mapping_admin?.available && (
                          <NavLink
                            component={Link}
                            to="/admin/steam-mappings"
                            label="Steam Mappings"
                            leftSection={<IconLink size={20} />}
                            active={location.pathname === '/admin/steam-mappings'}
                            variant="filled"
                            color="myColor"
                            onClick={() => handleNavigation('/admin/steam-mappings')}
                          />
                        )}
                        {features.steam_basic_completion_admin?.available && (
                          <NavLink
                            component={Link}
                            to="/admin/steam-basic-completions"
                            label="Steam Completion"
                            leftSection={<IconTrophy size={20} />}
                            active={location.pathname === '/admin/steam-basic-completions'}
                            variant="filled"
                            color="myColor"
                            onClick={() => handleNavigation('/admin/steam-basic-completions')}
                          />
                        )}
                        <NavLink
                          component={Link}
                          to="/backlog/dashboard"
                          label="Dashboard Hub"
                          leftSection={<IconLayoutDashboard size={20} />}
                          active={location.pathname === '/backlog/dashboard'}
                          variant="filled"
                          color="myColor"
                          onClick={() => handleNavigation('/backlog/dashboard')}
                        />
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </ScrollArea>
              <Box
                style={{
                  borderTop: '1px solid var(--mantine-color-gray-3)',
                  padding: '5px 0',
                  marginTop: 'auto',
                }}
              >
                <Text size="xs" c="dimmed" ta="center" mb={0} pb={0} pt={10}>
                  Core GameDB data by{' '}
                  <a
                    style={{ color: 'var(--mantine-color-blue-filled)' }}
                    href="https://igdb.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    IGDB.com
                  </a>
                </Text>
              </Box>
            </Box>
          )}
        </AppShell.Navbar>

        <AppShell.Main>
          {isAuthenticated && userState === 'active' ? (
            isGameList ? (
              <Suspense
                fallback={
                  <Center h="100%">
                    <Loader />
                  </Center>
                }
              >
                <Routes>
                  <Route
                    path="/collection"
                    element={<GameListContainer key="active" defaultStatus={ArticleStatus.Active} />}
                  />
                  <Route
                    path="/collection/wanted"
                    element={<GameListContainer key="wanted" defaultStatus={ArticleStatus.Wanted} />}
                  />
                  <Route
                    path="/collection/awaiting"
                    element={<GameListContainer key="awaiting" defaultStatus={ArticleStatus.PendingArrival} />}
                  />
                  <Route path="/logout" element={<LogoutHandler />} />
                </Routes>
              </Suspense>
            ) : (
              <div
                ref={contentRef}
                style={{
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: '100%',
                  position: 'relative',
                }}
              >
                <Suspense
                  fallback={
                    <Center h="100%">
                      <Loader />
                    </Center>
                  }
                >
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/gamedb/:id" element={<GameDbGamePage />} />
                    <Route path="/gamedb/company/:providerId/:providerCompanyId" element={<GameDbCompanyPage />} />
                    <Route path="/lists" element={<ListsLandingPage />} />
                    <Route path="/lists/:listId" element={<ListsPage />} />
                    <Route
                      path="/backlog"
                      element={<Navigate to={isDesktop ? '/backlog/kanban' : '/backlog/tabs'} replace />}
                    />
                    <Route path="/backlog/kanban" element={<BacklogKanbanPage />} />
                    <Route path="/backlog/tabs" element={<BacklogTabbedPage />} />
                    <Route path="/backlog/dashboard" element={<BacklogDashboardPage />} />
                    <Route path="/backlog/played" element={<BacklogPlayedHistoryPage />} />
                    <Route path="/backlog/played/:year" element={<BacklogYearReviewPage />} />
                    <Route path="/add" element={<ArticleWizard />} />
                    <Route path="/edit/:id" element={<ArticleDetailPage />} />
                    <Route path="/import" element={<Navigate to="/upload" replace />} />
                    <Route path="/upload" element={<UploadLandingPage />} />
                    <Route path="/upload/:uploadId" element={<UploadDetailsPage />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/profile" element={<UserProfile />} />
                    <Route path="/admin/overview" element={<AdminOverviewPage />} />
                    <Route path="/admin/cover-search" element={<CoverSearchTestPage />} />
                    <Route path="/admin/missing-wikipedia" element={<MissingWikipediaPage />} />
                    <Route path="/admin/jobs" element={<JobsPage />} />
                    <Route path="/admin/users" element={<UserAdminPage />} />
                    <Route path="/admin/users/:userId" element={<UserAdminDetailsPage />} />
                    <Route path="/admin/slug-collisions" element={<SlugCollisionsPage />} />
                    <Route
                      path="/admin/steam-mappings"
                      element={
                        features.steam_mapping_admin?.available ? <SteamMappingsPage /> : <Navigate to="/" replace />
                      }
                    />
                    <Route
                      path="/admin/steam-basic-completions"
                      element={
                        features.steam_basic_completion_admin?.available ? (
                          <SteamBasicCompletionsPage />
                        ) : (
                          <Navigate to="/" replace />
                        )
                      }
                    />
                    <Route path="/logout" element={<LogoutHandler />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </div>
            )
          ) : (
            <Suspense
              fallback={
                <Center h="100%">
                  <Loader />
                </Center>
              }
            >
              {location.pathname === '/gamedb' ? <NotFound /> : <Landing />}
            </Suspense>
          )}
        </AppShell.Main>
      </AppShell>
    </>
  );
}
